/**
 * 条件分支配置面板
 *
 * 配置条件分支列表和默认分支
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { ConditionNodeData, ConditionBranch } from '../../../types/workflow';
import { useWorkflowStore } from '../../../store/workflowStore';

export interface ConditionNodeConfigPanelProps {
  /** 节点 ID */
  nodeId: string;
}

// 生成唯一 ID
const generateId = () => `branch-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

export const ConditionNodeConfigPanel: React.FC<ConditionNodeConfigPanelProps> = React.memo(({
  nodeId,
}) => {
  // 从 store 获取节点数据
  const node = useWorkflowStore((state) =>
    state.nodes.find((n) => n.id === nodeId)
  );
  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 本地表单状态
  const [formData, setFormData] = useState<{
    label: string;
    branches: ConditionBranch[];
    default_branch_id: string | undefined;
  }>({
    label: '',
    branches: [],
    default_branch_id: undefined,
  });

  // 错误状态
  const [errors, setErrors] = useState<{
    branches?: string;
  }>({});

  // 同步节点数据到本地状态
  useEffect(() => {
    if (node) {
      const data = node.data as ConditionNodeData;
      setFormData({
        label: data.label || '条件分支',
        branches: data.branches || [],
        default_branch_id: data.default_branch_id,
      });
    }
  }, [node]);

  // 验证表单
  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (formData.branches.length === 0) {
      newErrors.branches = '至少需要定义一个条件分支';
    }

    // 检查是否有空标签
    const hasEmptyLabel = formData.branches.some((b) => !b.label.trim());
    if (hasEmptyLabel) {
      newErrors.branches = '所有分支标签不能为空';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存配置
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    updateNodeForm(nodeId, {
      label: formData.label,
      branches: formData.branches,
      default_branch_id: formData.default_branch_id,
    });
  }, [nodeId, formData, updateNodeForm, validateForm]);

  // 更新字段
  const updateField = useCallback(<K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除对应错误
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof typeof errors];
        return next;
      });
    }
  }, [errors]);

  // 添加分支
  const addBranch = useCallback(() => {
    const newBranch: ConditionBranch = {
      id: generateId(),
      label: `分支 ${formData.branches.length + 1}`,
      description: '',
    };
    updateField('branches', [...formData.branches, newBranch]);
  }, [formData.branches, updateField]);

  // 删除分支
  const removeBranch = useCallback((branchId: string) => {
    const newBranches = formData.branches.filter((b) => b.id !== branchId);
    updateField('branches', newBranches);

    // 如果删除的是默认分支，清除默认分支设置
    if (formData.default_branch_id === branchId) {
      updateField('default_branch_id', undefined);
    }
  }, [formData.branches, formData.default_branch_id, updateField]);

  // 更新分支
  const updateBranch = useCallback((branchId: string, updates: Partial<ConditionBranch>) => {
    const newBranches = formData.branches.map((b) =>
      b.id === branchId ? { ...b, ...updates } : b
    );
    updateField('branches', newBranches);
  }, [formData.branches, updateField]);

  // 设置默认分支
  const setDefaultBranch = useCallback((branchId: string) => {
    updateField('default_branch_id', branchId);
  }, [updateField]);

  // 上移分支
  const moveBranchUp = useCallback((index: number) => {
    if (index <= 0) return;
    const newBranches = [...formData.branches];
    [newBranches[index - 1], newBranches[index]] = [newBranches[index], newBranches[index - 1]];
    updateField('branches', newBranches);
  }, [formData.branches, updateField]);

  // 下移分支
  const moveBranchDown = useCallback((index: number) => {
    if (index >= formData.branches.length - 1) return;
    const newBranches = [...formData.branches];
    [newBranches[index], newBranches[index + 1]] = [newBranches[index + 1], newBranches[index]];
    updateField('branches', newBranches);
  }, [formData.branches, updateField]);

  return (
    <div className="p-4 space-y-4">
      {/* 节点名称 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          节点名称
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => updateField('label', e.target.value)}
          placeholder="条件分支"
          className={clsx(
            'w-full px-3 py-2 rounded-md border border-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent',
            'text-sm text-gray-700',
          )}
        />
      </div>

      {/* 条件分支列表 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            条件分支 <span className="text-red-500">*</span>
          </label>
          <button
            onClick={addBranch}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 text-xs font-medium',
              'text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded',
              'transition-colors',
            )}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            添加分支
          </button>
        </div>

        {errors.branches && (
          <p className="mb-2 text-sm text-red-500">{errors.branches}</p>
        )}

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {formData.branches.length === 0 ? (
            <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-md">
              点击"添加分支"创建条件分支
            </div>
          ) : (
            formData.branches.map((branch, index) => (
              <div
                key={branch.id}
                className={clsx(
                  'p-3 border rounded-md',
                  branch.id === formData.default_branch_id
                    ? 'border-gray-300 bg-gray-50'
                    : 'border-amber-200 bg-amber-50',
                )}
              >
                {/* 分支头部 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-amber-600 text-xs font-medium border border-amber-200">
                    {index + 1}
                  </span>
                  <input
                    type="text"
                    value={branch.label}
                    onChange={(e) => updateBranch(branch.id, { label: e.target.value })}
                    placeholder="分支标签"
                    className={clsx(
                      'flex-1 px-2 py-1 rounded border text-sm',
                      'focus:outline-none focus:ring-1 focus:border-transparent',
                      branch.id === formData.default_branch_id
                        ? 'border-gray-200 focus:ring-gray-400'
                        : 'border-amber-200 focus:ring-amber-400',
                    )}
                  />
                  {/* 操作按钮 */}
                  <div className="flex items-center gap-0.5">
                    {/* 上移 */}
                    <button
                      onClick={() => moveBranchUp(index)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    {/* 下移 */}
                    <button
                      onClick={() => moveBranchDown(index)}
                      disabled={index === formData.branches.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {/* 设置为默认 */}
                    <button
                      onClick={() => setDefaultBranch(branch.id)}
                      className={clsx(
                        'p-1 transition-colors',
                        branch.id === formData.default_branch_id
                          ? 'text-gray-500'
                          : 'text-gray-300 hover:text-gray-500',
                      )}
                      title={branch.id === formData.default_branch_id ? '默认分支' : '设为默认'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    {/* 删除 */}
                    <button
                      onClick={() => removeBranch(branch.id)}
                      className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                      title="删除"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* 分支描述 */}
                <textarea
                  value={branch.description}
                  onChange={(e) => updateBranch(branch.id, { description: e.target.value })}
                  placeholder="描述此分支的触发条件（LLM 会根据此描述判断）"
                  rows={2}
                  className={clsx(
                    'w-full px-2 py-1 rounded border text-xs resize-none',
                    'focus:outline-none focus:ring-1 focus:border-transparent',
                    'placeholder:text-gray-400',
                    branch.id === formData.default_branch_id
                      ? 'border-gray-200 focus:ring-gray-400 text-gray-500'
                      : 'border-amber-200 focus:ring-amber-400 text-gray-600',
                  )}
                />

                {/* 默认分支标记 */}
                {branch.id === formData.default_branch_id && (
                  <div className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    默认分支（当其他条件都不匹配时执行）
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 条件判断说明 */}
      <div className="p-3 bg-amber-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-700 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          条件判断说明
        </div>
        <div className="text-xs text-gray-500 space-y-1">
          <p>条件判断由 LLM 自动完成，根据上游节点的输出内容和各分支的描述进行匹配。</p>
          <p>建议设置一个默认分支，用于处理所有未匹配的情况。</p>
        </div>
      </div>

      {/* 保存按钮 */}
      <button
        onClick={handleSave}
        className={clsx(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-amber-500 text-white',
          'hover:bg-amber-600 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2',
        )}
      >
        保存配置
      </button>
    </div>
  );
});

ConditionNodeConfigPanel.displayName = 'ConditionNodeConfigPanel';

export default ConditionNodeConfigPanel;
